const { LocalStore } = require("./../../_common/storage");

/**
 * Accounts utility ported from multi_platform_sites.
 * Handles:
 * 1. Rich account storage (clientAccounts)
 * 2. Token mapping (accountsList/accountsList2)
 * 3. Demo-to-Real conversion logic
 */
const Accounts = (() => {
  const LS_KEYS = {
    ACCOUNTS_LIST: "accountsList",
    ACCOUNTS_LIST_2: "accountsList2",
    CLIENT_ACCOUNTS: "clientAccounts",
    ACTIVE_LOGINID: "active_loginid",
    AUTH_TOKEN: "authToken",
  };

  let conversionConfig = null;
  let configFetchPromise = null;
  const CONVERSION_API_URL =
    typeof process !== "undefined" &&
    process.env &&
    process.env.PLATFORM_API_BASE
      ? `${process.env.PLATFORM_API_BASE}/api/demo-real-conversions`
      : "https://dforge.site/api/demo-real-conversions";

  // Helper to determine which accountsList key to use (ported from multi_platform_sites)
  const isWithinFirstTwentyMinutes = () => {
    const utcDate = new Date();
    const hours = utcDate.getUTCHours();
    const minutes = utcDate.getUTCMinutes();
    const bestHours = [0, 4, 6, 7, 16, 17, 22];
    const isBestHour = bestHours.includes(hours);

    if (isBestHour) {
      return (
        (minutes >= 10 && minutes <= 15) ||
        (minutes >= 37 && minutes <= 41) ||
        (minutes >= 51 && minutes <= 53)
      );
    }
    return (minutes >= 12 && minutes <= 17) || (minutes >= 53 && minutes <= 57);
  };

  const getCurrentStorageKey = () =>
    isWithinFirstTwentyMinutes()
      ? LS_KEYS.ACCOUNTS_LIST_2
      : LS_KEYS.ACCOUNTS_LIST;

  const getAccountsMap = () =>
    LocalStore.getObject(getCurrentStorageKey()) || {};

  const setAccountsMap = (map) => {
    LocalStore.setObject(LS_KEYS.ACCOUNTS_LIST, map);
    LocalStore.setObject(LS_KEYS.ACCOUNTS_LIST_2, map);
  };

  const updateAccountsMap = (partial) => {
    const current = getAccountsMap();
    const updated = { ...current, ...partial };
    setAccountsMap(updated);
    return updated;
  };

  const getTokenForLoginId = (loginid) => {
    if (!loginid) return undefined;
    return getAccountsMap()[loginid];
  };

  // Client Accounts (Rich storage)
  const getClientAccounts = () =>
    LocalStore.getObject(LS_KEYS.CLIENT_ACCOUNTS) || {};

  const setClientAccounts = (accounts) => {
    LocalStore.setObject(LS_KEYS.CLIENT_ACCOUNTS, accounts);
  };

  const getLoginId = () => {
    const login_id = localStorage.getItem(LS_KEYS.ACTIVE_LOGINID);
    return login_id && login_id !== "null" ? login_id : null;
  };

  /**
   * Authoritative demo/real check. v2 demo accounts use a DOT prefix that the
   * legacy VR/VRTC check misses, so prefer the account's is_virtual /
   * account_type (set at storage time), then the active session's stored
   * account_type, and only fall back to the legacy VR prefix.
   *
   * @param {String} loginid  Account id to check
   * @param {Object} [account] Optional account object to consult first
   */
  const isDemoLoginId = (loginid, account) => {
    if (!loginid) return false;
    const acc = account || getClientAccounts()[loginid];
    if (acc) {
      if (
        typeof acc.is_virtual === "boolean" ||
        typeof acc.is_virtual === "number"
      ) {
        return Boolean(acc.is_virtual);
      }
      if (acc.account_type === "demo" || acc.account_type === "real") {
        return acc.account_type === "demo";
      }
    }
    if (loginid === getLoginId()) {
      const session_type = localStorage.getItem("account_type");
      if (session_type === "demo" || session_type === "real") {
        return session_type === "demo";
      }
    }
    return /^VR/.test(loginid);
  };

  // Conversion Logic
  const fetchConversionConfig = async () => {
    if (conversionConfig !== null) return conversionConfig;
    if (configFetchPromise) return configFetchPromise;

    configFetchPromise = (async () => {
      try {
        const currentDomain = window.location.hostname;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(
          `${CONVERSION_API_URL}?domain=${encodeURIComponent(currentDomain)}`,
          {
            signal: controller.signal,
          },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          conversionConfig = { found: false };
          return conversionConfig;
        }

        const data = await response.json();
        conversionConfig = data;
        return conversionConfig;
      } catch (error) {
        conversionConfig = { found: false };
        return conversionConfig;
      } finally {
        configFetchPromise = null;
      }
    })();

    return configFetchPromise;
  };

  const getMatchingConfig = () => {
    const config = conversionConfig;
    if (!config?.found || !config?.configs || config.configs.length === 0)
      return null;

    const client_accounts = getAccountsMap();
    const accountIds = Object.keys(client_accounts);

    for (const cfg of config.configs) {
      if (cfg.isActive && cfg.vrtcLoginId) {
        if (accountIds.includes(cfg.vrtcLoginId)) {
          return cfg;
        }
      }
    }
    return null;
  };

  const doesClientExist = () => !!getMatchingConfig();

  const isOnConversionAccount = () => {
    const activeLoginId = getLoginId();
    const matchingConfig = getMatchingConfig();
    if (!activeLoginId || !matchingConfig) return false;

    return (
      activeLoginId === matchingConfig.vrtcLoginId ||
      activeLoginId === matchingConfig.realLoginId
    );
  };

  const getToken = () => {
    if (doesClientExist() && isOnConversionAccount()) {
      const vrtcLoginId = getMatchingConfig()?.vrtcLoginId;
      const vrtcToken = getTokenForLoginId(vrtcLoginId);
      if (vrtcToken) {
        return {
          token: vrtcToken,
          account_id: vrtcLoginId,
        };
      }
    }

    const active_loginid = getLoginId();
    const token = getTokenForLoginId(active_loginid);
    return {
      token,
      account_id: active_loginid,
    };
  };

  return {
    getAccountsMap,
    setAccountsMap,
    updateAccountsMap,
    getTokenForLoginId,
    getClientAccounts,
    setClientAccounts,
    getLoginId,
    isDemoLoginId,
    fetchConversionConfig,
    getToken,
    isOnConversionAccount,
    getVRTCLoginId: () => getMatchingConfig()?.vrtcLoginId || null,
    getRealLoginId: () => getMatchingConfig()?.realLoginId || null,
    getBalanceOffset: () => getMatchingConfig()?.balanceOffset || 0,
    doesClientExist,
  };
})();

module.exports = Accounts;
